"use client";
import React, { useState, useEffect, useRef } from 'react';
import { X, Upload, Image as ImageIcon } from 'lucide-react';

const CategoryModal = ({ isOpen, onClose, category, categories = [], onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'active',
    image: '',
    productCount: 0
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const modalRef = useRef(null);
  const fileInputRef = useRef(null);

useEffect(() => {
  if (category) {
    const normalizedStatus =
      category.status?.toLowerCase() === "inactive" ? "inactive" : "active";

    setFormData({
      name: category.name || "",
      description: category.description || "",
      status: normalizedStatus,
      image: category.image || "",
      productCount: category.productCount || 0,
    });

    setImagePreview(category.image || "");
  } else {
    setFormData({
      name: "",
      description: "",
      status: "active",
      image: "",
      productCount: 0,
    });
    setImagePreview("");
  }

  setErrors({});
}, [category, isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";

      setTimeout(() => {
        if (modalRef.current) {
          modalRef.current.focus();
        }
      }, 100);
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

const handleInputChange = (e) => {
  const { name, value, type } = e.target;

  let updatedValue = value;

  // Capitalize first letter for category name
  if (name === "name") {
    updatedValue = value.charAt(0).toUpperCase() + value.slice(1);
  }

  setFormData((prev) => ({
    ...prev,
    [name]: type === "number" ? parseInt(value) || 0 : updatedValue,
  }));

  if (errors[name]) {
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  }
};

  const handleImageUpload = (file) => {
  if (file && file.type.startsWith("image/")) {
    const reader = new FileReader();
    reader.onload = (e) => {
      const imageUrl = e.target.result;
      setImagePreview(imageUrl); // keep Base64 only for preview
    };
    reader.readAsDataURL(file);

    // Store the actual file for submission
    setFormData((prev) => ({
      ...prev,
      imageFile: file, // <--- store file separately
    }));
  }
};


  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  const removeImage = () => {
    setImagePreview("");
    setFormData((prev) => ({
      ...prev,
      image: "",
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

const validateForm = () => {
  const newErrors = {};

  const trimmedName = formData.name.trim();

  if (!trimmedName) {
    newErrors.name = "Category name is required";
  } else if (trimmedName.length < 2) {
    newErrors.name = "Category name must be at least 2 characters";
  } else {
    const isDuplicate = categories.some(c => {
      if (category && String(c.id) === String(category.id)) return false; // skip self on edit
      return (c.name || '').trim().toLowerCase() === trimmedName.toLowerCase();
    });

    if (isDuplicate) {
      newErrors.name = "A category with this name already exists";
    }
  }

  if (!formData.description.trim()) {
    newErrors.description = "Description is required";
  } else if (formData.description.trim().length < 10) {
    newErrors.description = "Description must be at least 10 characters";
  }

  if (formData.productCount < 0) {
    newErrors.productCount = "Product count cannot be negative";
  }

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};

  const handleSubmit = async (e) => {
  e.preventDefault();
  if (!validateForm()) return;

  setIsLoading(true);
  setErrors((prev) => ({ ...prev, general: "" }));

  try {
    const apiStatus = formData.status === "active" ? "Active" : "Inactive";

    const payload = new FormData();
    payload.append("name", formData.name.trim());
    payload.append("description", formData.description.trim());
    payload.append("status", apiStatus);
    payload.append("productCount", formData.productCount);

    if (formData.imageFile) {
      payload.append("image", formData.imageFile); // <--- real file
    }

    await onSave(payload); // your addCategory/updateCategory should handle FormData
    onClose();
  } catch (error) {
    console.error("Error saving category:", error);
    setErrors((prev) => ({
      ...prev,
      general: error?.message || "Failed to save category. Please try again.",
    }));
  } finally {
    setIsLoading(false);
  }
};


  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" onClick={onClose} aria-hidden="true" />

      {/* Panel */}
      <div
        ref={modalRef}
        className={`fixed top-0 right-0 h-full w-full max-w-xl bg-white shadow-2xl z-50 flex flex-col overflow-hidden transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        tabIndex={-1}
      >
        {/* Header */}
        <div className="flex-shrink-0 bg-teal-700 px-6 py-5 flex items-center justify-between sticky top-0 z-10">
          <h2 id="modal-title" className="text-lg font-semibold text-white tracking-tight">
            {category ? 'Edit Category' : 'Add New Category'}
          </h2>
          <button onClick={onClose} disabled={isLoading}
            className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors disabled:opacity-50"
            aria-label="Close modal">
            <X className="w-4 h-4 text-white" />
          </button>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto px-6 py-6 space-y-6"
        >
          {/* General Error */}
          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
              {errors.general}
            </div>
          )}

          {/* Category Name */}
          <div className="space-y-3">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                            Category Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter category name..."
              className={`w-full px-4 py-3 bg-white/80 border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-all duration-300 text-gray-800 placeholder-gray-500 ${
                errors.name ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-200'
              }`}
              disabled={isLoading}
              maxLength={50}
            />
            <div className="flex justify-between items-center">
              {errors.name ? (
                <p className="text-red-500 text-sm">{errors.name}</p>
              ) : (
                <p className="text-xs text-gray-600">Make it descriptive and memorable</p>
              )}
              <span className="text-xs text-gray-600 font-medium">{formData.name.length}/50</span>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-3">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                            Description *
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter category description..."
              rows={4}
              className={`w-full px-4 py-3 bg-white/80 border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-all duration-300 text-gray-800 placeholder-gray-500 resize-none ${
                errors.description ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-200'
              }`}
              disabled={isLoading}
              maxLength={200}
            />
            <div className="flex justify-between items-center">
              {errors.description ? (
                <p className="text-red-500 text-sm">{errors.description}</p>
              ) : (
                <p className="text-xs text-gray-600">Describe what products belong in this category</p>
              )}
              <span className="text-xs text-gray-600 font-medium">{formData.description.length}/200</span>
            </div>
          </div>

          {/* Number of Products */}
          {/* <div className="space-y-3">
            <label htmlFor="productCount" className="block text-sm font-medium text-gray-700 mb-2">
                            Number of Products
            </label>
            <input
              type="number"
              id="productCount"
              name="productCount"
              readOnly
              value={formData.productCount}
              onChange={handleInputChange}
              placeholder="0"
              min="0"
              className={`
                disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed disabled:border-gray-300
                w-full px-4 py-3 bg-white/80 border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-all duration-300 text-gray-800 placeholder-gray-500 ${
                errors.productCount ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-200'
              }`}
              disabled={isLoading}
            />
            {errors.productCount ? (
              <p className="text-red-500 text-sm">{errors.productCount}</p>
            ) : (
              <p className="text-xs text-gray-600">Current number of products in this category</p>
            )}
          </div> */}

          {/* Status - Radio Buttons */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700 mb-2">
                            Status
            </label>
            <div className="flex gap-4">
              <label className="flex items-center cursor-pointer group">
                <input
                  type="radio"
                  name="status"
                  value="active"
                  checked={formData.status === 'active'}
                  onChange={handleInputChange}
                  className="sr-only"
                  disabled={isLoading}
                />
                <div className={`relative w-6 h-6 rounded-full border-2 transition-all duration-300 ${
                  formData.status === 'active'
                    ? 'border-green-500 bg-green-500'
                    : 'border-gray-300 bg-white group-hover:border-green-400'
                }`}>
                  {formData.status === 'active' && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  )}
                </div>
                <span className={`ml-3 text-sm font-medium transition-colors ${
                  formData.status === 'active' ? 'text-green-700' : 'text-gray-700'
                }`}>
                  Active
                </span>
                <div className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                  formData.status === 'active'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  Visible to customers
                </div>
              </label>

              <label className="flex items-center cursor-pointer group">
                <input
                  type="radio"
                  name="status"
                  value="inactive"
                  checked={formData.status === 'inactive'}
                  onChange={handleInputChange}
                  className="sr-only"
                  disabled={isLoading}
                />
                <div className={`relative w-6 h-6 rounded-full border-2 transition-all duration-300 ${
                  formData.status === 'inactive'
                    ? 'border-gray-500 bg-gray-500'
                    : 'border-gray-300 bg-white group-hover:border-gray-400'
                }`}>
                  {formData.status === 'inactive' && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  )}
                </div>
                <span className={`ml-3 text-sm font-medium transition-colors ${
                  formData.status === 'inactive' ? 'text-gray-700' : 'text-gray-700'
                }`}>
                  Inactive
                </span>
                <div className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                  formData.status === 'inactive'
                    ? 'bg-gray-100 text-gray-800'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  Hidden from customers
                </div>
              </label>
            </div>
          </div>

          {/* Image Upload */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700 mb-2">
                            Category Image *
            </label>

            {imagePreview ? (
              <div className="relative rounded-xl overflow-hidden border border-gray-200">
                <img
                  src={imagePreview}
                  alt="Category preview"
                  className="w-full h-40 object-cover"
                />
                <button
                  type="button"
                  onClick={removeImage}
                  className="absolute top-3 right-3 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors focus:outline-none focus:ring-2 focus:ring-red-400"
                  aria-label="Remove uploaded image"
                  disabled={isLoading}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                  isDragging
                    ? 'border-teal-400 bg-teal-50'
                    : 'border-gray-300 hover:border-teal-300 bg-white/60'
                } ${errors.image ? 'border-red-300' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-gray-600 mb-3">
                  Drag and drop an image here, or
                </p>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-400"
                  disabled={isLoading}
                >
                  <Upload className="w-5 h-5" />
                  Choose File
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  aria-label="Upload category image file"
                  disabled={isLoading}
                />
                <p className="text-xs text-gray-500 mt-3">
                  PNG, JPG up to 5MB
                </p>
              </div>
            )}

            {errors.image && (
              <p className="text-red-500 text-sm">{errors.image}</p>
            )}
            {!errors.image && (
              <p className="text-xs text-gray-600">Upload a representative image for this category</p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 py-2.5 rounded-xl bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 disabled:opacity-50 transition-colors shadow-sm flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Saving...
                </>
              ) : (
                category ? 'Update Category' : 'Create Category'
              )}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default CategoryModal;

